import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const SOURCE_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "bulk_lot", label: "Bulk Lot" },
  { value: "pack_rip", label: "Pack Rip" },
  { value: "targeted_buy", label: "Targeted Buy" },
];

const LISTING_STRATEGIES = [
  { value: "collectible_single", label: "Collectible Single" },
  { value: "trainer_single", label: "Trainer Single" },
  { value: "trainer_playset", label: "Trainer Playset" },
  { value: "trainer_both", label: "Trainer Both" },
  { value: "trainer_bulk", label: "Trainer Bulk" },
];

const CARD_VARIANTS = [
  { value: "Normal", label: "Normal" },
  { value: "Reverse Holo", label: "Reverse Holo" },
  { value: "Holo", label: "Holo" },
];

const buildEditableCard = (card) => ({
  purchase_request_id: card.purchase_request_id,
  card_name: card.card_name || "",
  quantity_ordered: card.quantity_ordered ?? 1,
  quantity_received: card.quantity_received ?? 0,
  unit_cost: card.unit_cost ?? card.agreed_price ?? "",
  source_type: card.source_type || "standard",
  listing_strategy: card.listing_strategy || "collectible_single",
  card_variant: card.card_variant || card.trainer_card_variant || "Normal",
  storage_location: card.storage_location || card.trainer_storage_location || "",
  receipt_notes: "",
});

const PurchaseOrders = () => {
  const [rows, setRows] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poCards, setPoCards] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [printingAll, setPrintingAll] = useState(false);
  const [saveAllInProgress, setSaveAllInProgress] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [printers, setPrinters] = useState([]);
  const [selectedPrinterKey, setSelectedPrinterKey] = useState("");
  const [draftsByPrId, setDraftsByPrId] = useState({});
  const [saveStatusByPrId, setSaveStatusByPrId] = useState({});
  const [bulkDefaults, setBulkDefaults] = useState({
    source_type: "bulk_lot",
    unit_cost: "0.015",
    listing_strategy: "trainer_single",
    card_variant: "Normal",
    storage_location: "",
  });

  useEffect(() => {
    axios.get("/api/purchase_orders").then((response) => {
      setRows(response.data || []);
    });

    axios
      .get("/api/label_printers")
      .then((res) => {
        const list = res.data?.printers || [];
        const def = res.data?.default_key || "";
        setPrinters(list);
        if (list.length > 0) {
          const keys = list.map((p) => p.key);
          setSelectedPrinterKey(def && keys.includes(def) ? def : list[0].key);
        }
      })
      .catch((err) => console.error("Printer load failed:", err));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSelectedPrinter = () => {
    if (!printers.length) return null;
    return printers.find((p) => p.key === selectedPrinterKey) || printers[0];
  };

  const getListingId = (card) =>
    card.listing_id ?? card.trainer_linked_listing_id ?? card.linked_listing_id ?? null;
  const getCardNumber = (card) => card.card_number ?? card.cardNumber ?? card.number ?? "-";

  const orderCards = (cards) =>
    cards
      .slice()
      .sort(
        (a, b) =>
          (a.purchase_request_id ?? a.id ?? 0) - (b.purchase_request_id ?? b.id ?? 0)
      );

  const delay = (ms) => new Promise((r) => setTimeout(r, ms));

  const handlePrintLabel = async (listingId, cardName, cardNumber) => {
    const printer = getSelectedPrinter();
    if (!printer) {
      alert("No printers configured.");
      return;
    }

    try {
      await axios.post(
        printer.target_url,
        {
          listing_id: Number(listingId),
          card_name: cardName,
          card_number: cardNumber,
          printer: printer.printer_name,
          job_uid: `single-${listingId}-${Date.now()}`,
        },
        { timeout: 15000 }
      );
      alert("Label sent");
    } catch (err) {
      alert(`Print failed: ${err.response?.data?.error || err.message}`);
    }
  };

  const handlePrintAllLabels = async () => {
    const printer = getSelectedPrinter();
    if (!printer) {
      alert("No printers configured.");
      return;
    }

    const queue = orderCards(poCards)
      .map((c) => ({
        listingId: getListingId(c),
        cardName: c.card_name ?? "",
        cardNumber: getCardNumber(c),
        prId: c.purchase_request_id ?? c.id ?? "x",
      }))
      .filter((j) => j.listingId);

    if (!queue.length) {
      alert("No cards in this PO have listings yet.");
      return;
    }

    setPrintingAll(true);
    let sent = 0;
    const failures = [];

    for (const job of queue) {
      try {
        await axios.post(
          printer.target_url,
          {
            listing_id: Number(job.listingId),
            card_name: job.cardName,
            card_number: job.cardNumber,
            printer: printer.printer_name,
            job_uid: `po${selectedPO}-pr${job.prId}-${Date.now()}`,
          },
          { timeout: 20000 }
        );
        sent += 1;
      } catch (err) {
        failures.push({
          id: job.listingId,
          err: err.response?.data?.error || err.message,
        });
        await delay(300);
      }
      await delay(900);
    }

    setPrintingAll(false);

    if (failures.length) {
      const msg = failures
        .slice(0, 6)
        .map((f) => `#${f.id}: ${f.err}`)
        .join("\n");
      alert(
        `Sent ${sent}/${queue.length} labels.\nFailures: ${failures.length}\n${msg}${
          failures.length > 6 ? "\n..." : ""
        }`
      );
    } else {
      alert(`Sent ${sent}/${queue.length} labels.`);
    }
  };


  const openRepricingForPO = (poId, tab = "po_performance") => {
    window.dispatchEvent(
      new CustomEvent("app:navigate-module", {
        detail: {
          module: "repricing",
          params: {
            repricing_tab: tab,
            repricing_mode: "po_cleanup",
            po_id: poId,
            purchase_order_id: poId,
            source: "purchase_orders",
          },
        },
      })
    );
  };

  const handleGenerateListings = (poId) => {
    axios
      .post(`/api/purchase_orders/${poId}/generate_listings`)
      .then((res) => {
        alert(`Listings created: ${res.data.created}`);
      })
      .catch((err) => {
        alert(`Error: ${err.response?.data?.error || err.message}`);
      });
  };

  const handleViewCards = (poId) => {
    axios
      .get(`/api/purchase_orders/${poId}/cards`)
      .then((response) => {
        const ordered = orderCards(response.data || []);
        setPoCards(ordered);
        setSelectedPO(poId);
        setDraftsByPrId(
          ordered.reduce((acc, card) => {
            acc[card.purchase_request_id] = buildEditableCard(card);
            return acc;
          }, {})
        );
        setSaveStatusByPrId({});
        setOpenModal(true);
      })
      .catch((error) => {
        console.error("Error fetching PO cards:", error);
        setPoCards([]);
        setSelectedPO(poId);
        setDraftsByPrId({});
        setSaveStatusByPrId({});
        setOpenModal(true);
      });
  };

  const formatDateTime = (raw) => {
    if (!raw) return "";
    const date = new Date(raw);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-AU", { month: "long" });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const updateDraftField = (purchaseRequestId, field, value) => {
    setDraftsByPrId((prev) => ({
      ...prev,
      [purchaseRequestId]: {
        ...prev[purchaseRequestId],
        [field]: value,
      },
    }));
  };

  const applyDefaultsToAll = () => {
    setDraftsByPrId((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = {
          ...next[key],
          source_type: bulkDefaults.source_type,
          listing_strategy: bulkDefaults.listing_strategy,
          card_variant: bulkDefaults.card_variant,
          unit_cost:
            next[key].unit_cost === "" || next[key].unit_cost === null
              ? bulkDefaults.unit_cost
              : next[key].unit_cost,
          storage_location: next[key].storage_location || bulkDefaults.storage_location,
        };
      });
      return next;
    });
  };

  const receiveAllOrdered = () => {
    setDraftsByPrId((prev) => {
      const next = { ...prev };
      orderCards(poCards).forEach((card) => {
        const existing = next[card.purchase_request_id] || buildEditableCard(card);
        next[card.purchase_request_id] = {
          ...existing,
          quantity_received: existing.quantity_ordered ?? card.quantity_ordered ?? 0,
          unit_cost:
            existing.unit_cost === "" || existing.unit_cost === null
              ? (card.agreed_price ?? existing.unit_cost)
              : existing.unit_cost,
        };
      });
      return next;
    });
  };

  const receiveOrderedForCard = (purchaseRequestId) => {
    const card = poCards.find((item) => item.purchase_request_id === purchaseRequestId);
    setDraftsByPrId((prev) => ({
      ...prev,
      [purchaseRequestId]: {
        ...(prev[purchaseRequestId] || buildEditableCard(card || {})),
        quantity_received:
          prev[purchaseRequestId]?.quantity_ordered ?? card?.quantity_ordered ?? 0,
        unit_cost:
          prev[purchaseRequestId]?.unit_cost === "" || prev[purchaseRequestId]?.unit_cost === null
            ? (card?.agreed_price ?? prev[purchaseRequestId]?.unit_cost ?? "")
            : prev[purchaseRequestId]?.unit_cost,
      },
    }));
  };

  const mergeSavedCard = (savedPrId, inventorySummary) => {
    setPoCards((prev) =>
      prev.map((card) =>
        card.purchase_request_id === savedPrId
          ? {
              ...card,
              ...draftsByPrId[savedPrId],
              total_cost:
                Number(draftsByPrId[savedPrId]?.quantity_received || 0) *
                Number(draftsByPrId[savedPrId]?.unit_cost || 0),
              trainer_inventory_summary_id: inventorySummary?.id,
              trainer_quantity_on_hand: inventorySummary?.quantity_on_hand,
              trainer_quantity_available: inventorySummary?.quantity_available,
              trainer_avg_unit_cost: inventorySummary?.avg_unit_cost,
              trainer_storage_location: inventorySummary?.storage_location,
              trainer_listing_strategy: inventorySummary?.listing_strategy,
              trainer_linked_listing_id: inventorySummary?.linked_listing_id,
              trainer_card_variant: inventorySummary?.card_variant,
            }
          : card
      )
    );
  };

  const saveCardReceipt = async (purchaseRequestId) => {
    if (!selectedPO) return;
    setSaveStatusByPrId((prev) => ({ ...prev, [purchaseRequestId]: "saving" }));
    try {
      const payload = draftsByPrId[purchaseRequestId];
      const response = await axios.post(
        `/api/purchase_orders/${selectedPO}/receive_inventory`,
        payload
      );
      mergeSavedCard(purchaseRequestId, response.data?.inventory_summary);
      setSaveStatusByPrId((prev) => ({ ...prev, [purchaseRequestId]: "saved" }));
    } catch (error) {
      console.error("Receipt save failed", error);
      setSaveStatusByPrId((prev) => ({
        ...prev,
        [purchaseRequestId]: error.response?.data?.error || "error",
      }));
    }
  };

  const saveAllReceipts = async () => {
    setSaveAllInProgress(true);
    for (const card of orderCards(poCards)) {
      // eslint-disable-next-line no-await-in-loop
      await saveCardReceipt(card.purchase_request_id);
    }
    setSaveAllInProgress(false);
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "supplier_name", headerName: "Supplier", width: 200 },
    { field: "status", headerName: "Status", width: 140 },
    { field: "notes", headerName: "Notes", width: 260 },
    { field: "total_value", headerName: "Planned Value", width: 130, type: "number" },
    {
      field: "created_at",
      headerName: "Created At",
      width: 180,
      renderCell: (params) => formatDateTime(params.row?.created_at),
    },
    {
      field: "repricing",
      headerName: "Repricing",
      width: 210,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.75}>
          <Button size="small" variant="outlined" onClick={() => openRepricingForPO(params.row.id, "po_performance")}>
            Review
          </Button>
          <Button size="small" variant="contained" color="secondary" onClick={() => openRepricingForPO(params.row.id, "campaign_modes")}>
            Run
          </Button>
        </Stack>
      ),
    },
    {
      field: "viewCards",
      headerName: "Cards",
      width: 120,
      renderCell: (params) => (
        <Button variant="outlined" onClick={() => handleViewCards(params.row.id)}>
          View Cards
        </Button>
      ),
    },
    {
      field: "generateListings",
      headerName: "Generate Listings",
      width: 180,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleGenerateListings(params.row.id)}
        >
          Generate Listings
        </Button>
      ),
    },
  ];

  const filteredRows = rows.filter((row) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      String(row.id).toLowerCase().includes(term) ||
      (row.supplier_name || "").toLowerCase().includes(term) ||
      (row.status || "").toLowerCase().includes(term) ||
      (row.notes || "").toLowerCase().includes(term)
    );
  });

  const modalTotals = useMemo(() => {
    return orderCards(poCards).reduce(
      (acc, card) => {
        const draft = draftsByPrId[card.purchase_request_id] || buildEditableCard(card);
        const qtyOrdered = Number(draft.quantity_ordered || 0);
        const qtyReceived = Number(draft.quantity_received || 0);
        const unitCost = Number(draft.unit_cost || 0);
        acc.ordered += qtyOrdered;
        acc.received += qtyReceived;
        acc.actualValue += qtyReceived * unitCost;
        return acc;
      },
      { ordered: 0, received: 0, actualValue: 0 }
    );
  }, [poCards, draftsByPrId]);



  return (
    <Box sx={{ height: "80vh", width: "100%" }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Purchase Orders
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextField
            label="Search Purchase Orders"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
      </Grid>

      <DataGrid rows={filteredRows} columns={columns} getRowId={(row) => row.id} />

      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        Trainer inventory controls now live in the separate Trainer Inventory module so Purchase Orders can stay focused on order receipt, reconciliation, and listing handoff.
      </Alert>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: 24,
            p: 3,
            width: { xs: "95vw", md: 1100 },
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
              <Box>
                <Typography variant="h6">Purchase Order #{selectedPO} Inventory Receipt</Typography>
                <Typography variant="body2" color="text.secondary">
                  Capture quantity, source type, cost layer, and Trainer storage/location without leaving the PO workflow.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Printer</InputLabel>
                  <Select
                    value={selectedPrinterKey}
                    label="Printer"
                    onChange={(e) => setSelectedPrinterKey(e.target.value)}
                  >
                    {printers.map((p) => (
                      <MenuItem key={p.key} value={p.key}>
                        {p.display_name || p.printer_name || p.key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePrintAllLabels}
                  disabled={printingAll || !poCards.some((c) => getListingId(c))}
                >
                  {printingAll ? "Printing..." : "Print All Labels"}
                </Button>
                <Button variant="outlined" color="secondary" onClick={() => openRepricingForPO(selectedPO, "campaign_modes")}>
                  PO Repricing
                </Button>
                <Button variant="contained" onClick={saveAllReceipts} disabled={saveAllInProgress || !poCards.length}>
                  {saveAllInProgress ? "Saving..." : "Save All Receipts"}
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Alert severity="info">Ordered units: {modalTotals.ordered}</Alert>
              </Grid>
              <Grid item xs={12} md={4}>
                <Alert severity="success">Received units: {modalTotals.received}</Alert>
              </Grid>
              <Grid item xs={12} md={4}>
                <Alert severity="warning">Actual received value: ${modalTotals.actualValue.toFixed(2)}</Alert>
              </Grid>
            </Grid>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Bulk defaults for sorted Trainer stock
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Source Type</InputLabel>
                      <Select
                        value={bulkDefaults.source_type}
                        label="Source Type"
                        onChange={(e) =>
                          setBulkDefaults((prev) => ({ ...prev, source_type: e.target.value }))
                        }
                      >
                        {SOURCE_TYPES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Default Unit Cost"
                      value={bulkDefaults.unit_cost}
                      onChange={(e) =>
                        setBulkDefaults((prev) => ({ ...prev, unit_cost: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Listing Strategy</InputLabel>
                      <Select
                        value={bulkDefaults.listing_strategy}
                        label="Listing Strategy"
                        onChange={(e) =>
                          setBulkDefaults((prev) => ({ ...prev, listing_strategy: e.target.value }))
                        }
                      >
                        {LISTING_STRATEGIES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Default Variant / Finish</InputLabel>
                      <Select
                        value={bulkDefaults.card_variant}
                        label="Default Variant / Finish"
                        onChange={(e) =>
                          setBulkDefaults((prev) => ({ ...prev, card_variant: e.target.value }))
                        }
                      >
                        {CARD_VARIANTS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Default Storage Box"
                      value={bulkDefaults.storage_location}
                      onChange={(e) =>
                        setBulkDefaults((prev) => ({ ...prev, storage_location: e.target.value }))
                      }
                    />
                  </Grid>
                </Grid>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 2 }}>
                  <Button variant="outlined" onClick={applyDefaultsToAll}>
                    Apply Defaults To All Rows
                  </Button>
                  <Button variant="contained" color="secondary" onClick={receiveAllOrdered}>
                    Receive All Ordered
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <Divider />

            <Stack spacing={2}>
              {orderCards(poCards).map((card) => {
                const draft = draftsByPrId[card.purchase_request_id] || buildEditableCard(card);
                const listingId = getListingId(card);
                const saveStatus = saveStatusByPrId[card.purchase_request_id];
                const cardNumber = getCardNumber(card);

                return (
                  <Card key={card.purchase_request_id} variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                          gap={1}
                        >
                          <Box>
                            <Typography variant="subtitle1">{card.card_name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              PR #{card.purchase_request_id} | Listing {listingId ?? "-"} | Number {cardNumber}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip label={`Predicted $${card.predicted_sale_price ?? 0}`} size="small" />
                            <Chip label={`Agreed $${card.agreed_price ?? 0}`} size="small" />
                            {saveStatus === "saving" && <Chip label="Saving" color="info" size="small" />}
                            {saveStatus === "saved" && <Chip label="Saved" color="success" size="small" />}
                            {saveStatus && !["saving", "saved"].includes(saveStatus) && (
                              <Chip label={String(saveStatus).slice(0, 30)} color="error" size="small" />
                            )}
                            {listingId && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handlePrintLabel(listingId, card.card_name ?? "", cardNumber)}
                              >
                                Print Label
                              </Button>
                            )}
                          </Stack>
                        </Stack>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Qty Ordered"
                              value={draft.quantity_ordered}
                              onChange={(e) =>
                                updateDraftField(card.purchase_request_id, "quantity_ordered", e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Qty Received"
                              value={draft.quantity_received}
                              onChange={(e) =>
                                updateDraftField(card.purchase_request_id, "quantity_received", e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Unit Cost"
                              value={draft.unit_cost}
                              onChange={(e) =>
                                updateDraftField(card.purchase_request_id, "unit_cost", e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Source Type</InputLabel>
                              <Select
                                value={draft.source_type}
                                label="Source Type"
                                onChange={(e) =>
                                  updateDraftField(card.purchase_request_id, "source_type", e.target.value)
                                }
                              >
                                {SOURCE_TYPES.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Listing Strategy</InputLabel>
                              <Select
                                value={draft.listing_strategy}
                                label="Listing Strategy"
                                onChange={(e) =>
                                  updateDraftField(card.purchase_request_id, "listing_strategy", e.target.value)
                                }
                              >
                                {LISTING_STRATEGIES.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Variant / Finish</InputLabel>
                              <Select
                                value={draft.card_variant || "Normal"}
                                label="Variant / Finish"
                                onChange={(e) =>
                                  updateDraftField(card.purchase_request_id, "card_variant", e.target.value)
                                }
                              >
                                {CARD_VARIANTS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Storage Box / Location"
                              value={draft.storage_location}
                              onChange={(e) =>
                                updateDraftField(card.purchase_request_id, "storage_location", e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Receipt Notes"
                              value={draft.receipt_notes}
                              onChange={(e) =>
                                updateDraftField(card.purchase_request_id, "receipt_notes", e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Alert severity="info">
                              Row total: ${(
                                Number(draft.quantity_received || 0) * Number(draft.unit_cost || 0)
                              ).toFixed(2)}
                            </Alert>
                          </Grid>
                        </Grid>

                        <Alert severity="info" icon={false}>
                          Trainer stock view — Variant: {card.trainer_card_variant || draft.card_variant || "Normal"} | On hand: {card.trainer_quantity_on_hand ?? 0} | Available: {card.trainer_quantity_available ?? 0} | Avg cost: {card.trainer_avg_unit_cost ?? 0} | Storage: {card.trainer_storage_location || "-"} | Linked listing: {card.trainer_linked_listing_id ?? "-"}
                        </Alert>

                        <Stack direction="row" spacing={1}>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            variant="outlined"
                            onClick={() => receiveOrderedForCard(card.purchase_request_id)}
                          >
                            Set Received = Ordered
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => saveCardReceipt(card.purchase_request_id)}
                          >
                            Save Receipt / Top Up Inventory
                          </Button>
                        </Stack>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {poCards.length === 0 && (
                <Typography variant="body2">No cards found or unable to fetch data.</Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default PurchaseOrders;



